import React, { FC, useEffect } from 'react';

import {
  type SearchParamsPetCounts,
  SearchParamsPetType,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import Banner from '@rover/kibble/official-patterns/Banner';
import type { CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrls';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import {
  BookingFeeLinkClickProfileEvent,
  SitterProfilePriceDisclaimerShownEvent,
} from './analyticsEvents';
import BannerContent from './BannerContent';

type VersionType = 'revamp' | 'legacy';

type SitterPriceBannerProps = {
  petCounts: SearchParamsPetCounts;
  petType?: SearchParamsPetType;
  serviceType?: string;
  countryConfigUrls?: CountryConfigurationUrls;
  bookingFeePercentage: string;
  services?: EnhancedPersonSitterProfileService[];
  version?: VersionType;
  /**
   * Drops the bottom margin and the rounded corners, for a container that bleeds
   * the banner out to its own edge and has nothing below it. Left off, the banner
   * keeps the spacing it needs to sit among other content.
   */
  isFlush?: boolean;
};

const FLUSH_CONTAINER_PROPS = { mb: '0x', borderRadius: '0px' } as const;

export const SitterPriceBanner: FC<SitterPriceBannerProps> = ({
  petCounts,
  petType,
  serviceType,
  countryConfigUrls,
  bookingFeePercentage,
  services,
  version = 'legacy',
  isFlush = false,
}) => {
  useEffect(() => {
    // Emit “sitter-profile-price-disclaimer-shown” event on Banner render
    emitAnalyticsEvent(new SitterProfilePriceDisclaimerShownEvent());
  }, []);

  const handleBookingFeeClick = (): void => {
    emitAnalyticsEvent(new BookingFeeLinkClickProfileEvent());
  };

  return (
    <Banner
      severity="info"
      hideIcon
      suppressDefaultTypography
      containerProps={{
        width: '100%',
        mb: version === 'revamp' ? '1x' : ['10x', '6x'],
        // Spread last so it wins over the margin above.
        ...(isFlush ? FLUSH_CONTAINER_PROPS : {}),
      }}
    >
      <BannerContent
        onBookingFeeClick={handleBookingFeeClick}
        bookingFeePercentage={bookingFeePercentage}
        countryConfigUrls={countryConfigUrls}
        petCounts={petCounts}
        petType={petType}
        serviceType={serviceType}
        services={services}
      />
    </Banner>
  );
};
